import { cleanQuote } from './quote';

/**
 * What the server said was in front of the model for an answer (its ground,
 * groundClient), as Firestore stores it: each window { from, to }, never
 * [from, to]. Firestore refuses an array inside an array, and the whole save
 * with it.
 */
function storableGround(g) {
  if (!g || typeof g !== 'object' || !Array.isArray(g.book)) return null;
  const book = g.book.filter((n) => Number.isInteger(n) && n >= 0 && n <= 30).slice(0, 20);
  const at = {};
  Object.entries(g.at || {}).forEach(([ch, list]) => {
    if (!book.includes(Number(ch)) || !Array.isArray(list)) return;
    const windows = list.slice(0, 6).map((w) => {
      const [from, to] = Array.isArray(w) ? w : w && typeof w === 'object' ? [w.from, w.to] : [];
      return Number.isInteger(from) ? { from, to: Number.isInteger(to) ? to : from } : null;
    }).filter(Boolean);
    if (windows.length) at[ch] = windows;
  });
  return { book, at };
}

/**
 * One message as a saved conversation keeps it (firebase/db.js
 * saveChatWithMessages). Firestore rejects `undefined`, so only the fields
 * worth storing, each only when it has a value. A person's turn keeps the
 * words it quoted ("Ask about this", utils/quote.js) when they still pass
 * the quote's rules; a bad one is dropped, never stored.
 */
export function cleanMessage(m) {
  const out = {
    role: m.role,
    content: m.content,
    timestamp: m.timestamp || new Date().toISOString(),
  };
  if (m.tier) out.tier = m.tier;
  if (m.model) out.model = m.model;
  if (m.rerun) out.rerun = true;
  // An answer's request id and speech grant: Listen on a reopened conversation reads in Rob's
  // voice through the server's speech guard (utils/voice.js), as it did when the answer came.
  if (m.role === 'assistant' && typeof m.turn === 'string' && typeof m.grant === 'string') {
    out.turn = m.turn;
    out.grant = m.grant;
  }
  if (Array.isArray(m.sources) && m.sources.length) {
    out.sources = m.sources.slice(0, 8).map((s) => ({
      videoId: s.videoId || null,
      title: s.title || null,
      publishDate: s.publishDate || null,
      videoUrl: s.videoUrl || null,
      timecode: s.timecode || null,
      // A chapter of the book, with where the reader opens and the quotes the server verified in it.
      ...(s.contentType ? { contentType: String(s.contentType).slice(0, 12) } : {}),
      ...(Number.isInteger(s.chapter) ? { chapter: s.chapter } : {}),
      ...(Number.isInteger(s.para) ? { para: s.para } : {}),
      ...(Number.isInteger(s.paraEnd) ? { paraEnd: s.paraEnd } : {}),
      ...(Array.isArray(s.quotes) && s.quotes.length ? { quotes: s.quotes.filter((q) => typeof q === 'string').slice(0, 2).map((q) => q.slice(0, 400)) } : {}),
    }));
  }
  // What was in front of the model (groundClient) and the checks' code: an answer reopened
  // later still numbers only the chapters it read.
  const ground = m.role === 'assistant' ? storableGround(m.ground) : null;
  if (ground) out.ground = ground;
  if (m.role === 'assistant' && m.checks && typeof m.checks === 'object') {
    out.checks = { note: typeof m.checks.note === 'string' ? m.checks.note.slice(0, 40) : null };
  }
  const quote = m.role === 'user' ? cleanQuote(m.quote) : null;
  if (quote) out.quote = quote;
  return out;
}
